/**
 * PersonaRow — one persona's expandable card: header (name/code/group chip/
 * user count/Move), module matrix (own + inherited from its group, if any),
 * and the AI-template assignment. Extracted from PersonasPage.tsx so it can
 * be reused both by the flat "All personas"/"Ungrouped" lists AND by a
 * group's Members section (Persona Groups, step 4).
 */
import { useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { ChevronDown, ChevronRight, Pencil, ArrowRight } from "lucide-react";

import ModuleMatrix from "./ModuleMatrix";
import UpsertModuleModal from "./UpsertModuleModal";
import type { SelectOption } from "./SearchableSelect";
import SearchableSelect from "./SearchableSelect";
import {
  UPSERT_PERSONA_MODULE_MUTATION,
  DEACTIVATE_PERSONA_MODULE_MUTATION,
  UPSERT_PERSONA_TEMPLATE_MUTATION,
  type AcAdminPersona,
  type AcAdminPersonaModule,
  type AcPersonaGroup,
  type DeactivatePersonaModuleResult,
  type DeactivatePersonaModuleVars,
  type PersonaTemplate,
  type UpsertPersonaModuleVars,
  type UpsertPersonaTemplateVars,
} from "../graphql/adminOps";
import {
  DEFAULT_TAB_ACTION,
  computeDefaultTabUpserts,
  computeEffectiveCounts,
  resolveEffectiveModules,
  type UserCountState,
} from "../lib/personaGroupLogic";

// ---- Persona AI-template assignment (unchanged from the pre-extraction version) ----
const PERSONA_TEMPLATE_PURPOSE = "patient-summary";

function PersonaTemplatesSection({
  personaCode,
  assignments,
  templateOptions,
  onRefresh,
}: {
  personaCode: string;
  assignments: PersonaTemplate[];
  templateOptions: SelectOption[];
  onRefresh: () => void;
}) {
  const [upsert, { loading, error }] = useMutation<unknown, UpsertPersonaTemplateVars>(UPSERT_PERSONA_TEMPLATE_MUTATION, {
    onCompleted: onRefresh,
  });

  const current = assignments.find((a) => a.purpose === PERSONA_TEMPLATE_PURPOSE) ?? assignments[0];

  const onChange = (templateId: string | null) => {
    if (!templateId || templateId === current?.templateId) return;
    void upsert({ variables: { input: { personaCode, purpose: PERSONA_TEMPLATE_PURPOSE, templateId } } });
  };

  return (
    <div className="border-t border-border bg-muted/10 px-4 py-3">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground shrink-0">AI Template</label>
        <div className="flex-1 max-w-md">
          <SearchableSelect
            value={current?.templateId ?? null}
            onChange={onChange}
            options={templateOptions}
            ariaLabel={`AI template for ${personaCode}`}
            placeholder="Select a template…"
          />
        </div>
        {loading && <span className="text-xs text-muted-foreground">Saving…</span>}
      </div>
      {error && <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2 mt-2">{error.message}</p>}
    </div>
  );
}

export interface PersonaRowProps {
  persona: AcAdminPersona;
  /** The persona's group, if it has one (drives inherited pills + the group chip). */
  group: AcPersonaGroup | null;
  moduleIdOptions: SelectOption[];
  actionCatalog: string[];
  lobOptions: SelectOption[];
  templateOptions: SelectOption[];
  personaTemplates: PersonaTemplate[];
  /** Users currently holding this persona; undefined = not requested yet. */
  userCount?: UserCountState;
  showGroupChip?: boolean;
  selectable?: boolean;
  picked?: boolean;
  onTogglePick?: () => void;
  onRequestMove?: () => void;
  flash?: boolean;
  onRefresh: () => void;
}

export default function PersonaRow({
  persona,
  group,
  moduleIdOptions,
  actionCatalog,
  lobOptions,
  templateOptions,
  personaTemplates,
  userCount,
  showGroupChip = true,
  selectable,
  picked,
  onTogglePick,
  onRequestMove,
  flash,
  onRefresh,
}: PersonaRowProps) {
  const client = useApolloClient();
  const [expanded, setExpanded] = useState(false);
  const [editTarget, setEditTarget] = useState<{ module?: AcAdminPersonaModule } | null>(null);
  const [defaultTabBusy, setDefaultTabBusy] = useState(false);
  const [defaultTabError, setDefaultTabError] = useState<string | null>(null);
  // Cross-write serialization (reorder ⇄ default-tab) — see ModuleMatrix's
  // onReorderBusyChange doc comment. Both writes upsert a module using ITS
  // OWN sortOrder/actions read at call time; if one's mutations are still in
  // flight when the other starts, the second can carry a stale value and
  // clobber what the first just persisted.
  const [reorderInFlight, setReorderInFlight] = useState(false);

  const [upsertModule] = useMutation<unknown, UpsertPersonaModuleVars>(UPSERT_PERSONA_MODULE_MUTATION, {
    onCompleted: onRefresh,
  });
  const [deactivateModule] = useMutation<DeactivatePersonaModuleResult, DeactivatePersonaModuleVars>(
    DEACTIVATE_PERSONA_MODULE_MUTATION,
    { onCompleted: onRefresh },
  );

  const activeCount = persona.modules.filter((m) => m.isActive).length;
  const nextSortOrder = persona.modules.reduce((max, m) => Math.max(max, m.sortOrder), -1) + 1;

  const rows = resolveEffectiveModules(persona.modules, group?.modules);
  const counts = computeEffectiveCounts(rows);

  const currentDefaultTabId = persona.modules.find((m) => (m.actions ?? []).includes(DEFAULT_TAB_ACTION))?.moduleId ?? null;

  const setDefaultTab = async (moduleId: string) => {
    // Refuse to start while a reorder's mutation batch is still in flight —
    // see the reorderInFlight declaration above.
    if (reorderInFlight) return;
    const target = currentDefaultTabId === moduleId ? null : moduleId;
    const upserts = computeDefaultTabUpserts(persona.code, persona.modules, target);
    if (upserts.length === 0) return;
    setDefaultTabBusy(true);
    try {
      const results = await Promise.allSettled(
        upserts.map((variables) => client.mutate<unknown, UpsertPersonaModuleVars>({ mutation: UPSERT_PERSONA_MODULE_MUTATION, variables })),
      );
      setDefaultTabError(results.some((r) => r.status === "rejected") ? "Couldn't save the default tab change. Please try again." : null);
    } finally {
      setDefaultTabBusy(false);
    }
    onRefresh();
  };

  return (
    <>
      <div className={"border border-border rounded-xl overflow-hidden mb-3 " + (flash ? "animate-pulse" : "")}>
        <div className="w-full flex items-center gap-3 px-4 py-3 bg-card hover:bg-muted/30 transition-colors">
          {selectable && (
            <input
              type="checkbox"
              checked={!!picked}
              onChange={onTogglePick}
              aria-label={`Select ${persona.displayName}`}
              className="rounded border-input accent-primary shrink-0"
            />
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-3 flex-1 min-w-0 bg-transparent border-0 text-left"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <span className="font-semibold">{persona.displayName}</span>
            <span className="text-xs text-muted-foreground font-mono">{persona.code}</span>
          </button>
          <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            {showGroupChip &&
              (group ? (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold bg-primary/10 text-primary border border-dashed border-primary/45">
                  {group.displayName}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted text-muted-foreground border border-border">
                  Ungrouped
                </span>
              ))}
            <span
              title={
                group
                  ? `${counts.inheritedModules} modules inherited from ${group.displayName}; ${counts.ownModules} own modules and ${counts.ownActions} extra actions on top`
                  : `${activeCount}/${persona.modules.length} active`
              }
            >
              {group ? `inherits ${counts.inheritedModules} · ${counts.additions} addition${counts.additions === 1 ? "" : "s"}` : `${activeCount}/${persona.modules.length} active`}
            </span>
            {userCount !== undefined && (
              <span title="Users currently holding this persona">
                {typeof userCount === "number" ? `${userCount} users` : userCount === "loading" ? "… users" : "users: unknown"}
              </span>
            )}
            {onRequestMove && (
              <button
                type="button"
                onClick={onRequestMove}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
              >
                <ArrowRight className="w-3 h-3" aria-hidden="true" />
                Move
              </button>
            )}
          </span>
        </div>

        {expanded && (
          <>
            <div className="border-t border-border bg-muted/10 px-4 py-3">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">
                  Modules{" "}
                  <span className="text-xs">(drag to reorder, toggle right/left to activate)</span>
                </p>
                <button
                  type="button"
                  onClick={() => setEditTarget({})}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  Add Module
                </button>
              </div>
              {defaultTabError && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2 mb-3" role="alert">
                  {defaultTabError}
                </p>
              )}
              {group && (
                <p className="text-xs text-muted-foreground mb-2">
                  Dashed rows and locked pills come from <b className="font-semibold text-foreground">{group.displayName}</b>. Add
                  on top here; to remove them, edit the group.
                </p>
              )}
              <ModuleMatrix
                scope={{ kind: "persona", code: persona.code, group: group ? { code: group.code, displayName: group.displayName } : undefined }}
                ownModules={persona.modules}
                groupModules={group?.modules}
                handlers={{
                  upsertModule: (vars) => upsertModule({ variables: { personaCode: persona.code, ...vars } }),
                  deactivateModule: (moduleId) => deactivateModule({ variables: { personaCode: persona.code, moduleId } }),
                }}
                onRefresh={onRefresh}
                onEditModule={(mod) => setEditTarget({ module: mod })}
                defaultTab={{
                  currentModuleId: currentDefaultTabId,
                  busy: defaultTabBusy || reorderInFlight,
                  onToggle: (id) => void setDefaultTab(id),
                }}
                onReorderBusyChange={setReorderInFlight}
              />
            </div>
            <PersonaTemplatesSection
              personaCode={persona.code}
              assignments={personaTemplates}
              templateOptions={templateOptions}
              onRefresh={onRefresh}
            />
          </>
        )}
      </div>
      {editTarget && (
        <UpsertModuleModal
          subjectLabel={`Persona: ${persona.code}`}
          existingModule={editTarget.module}
          nextSortOrder={nextSortOrder}
          moduleIdOptions={moduleIdOptions}
          actionCatalog={actionCatalog}
          lobOptions={lobOptions}
          onClose={() => setEditTarget(null)}
          onSubmit={(vars) => upsertModule({ variables: { personaCode: persona.code, ...vars } }).then(onRefresh)}
        />
      )}
    </>
  );
}
